import { getIn } from "icepick";
import { t } from "ttag";
import _ from "underscore";

import { displayNameForColumn } from "metabase/utils/formatting";
import { ChartSettingIconRadio } from "metabase/visualizations/components/settings/ChartSettingIconRadio";
import { ChartSettingsTableFormatting } from "metabase/visualizations/components/settings/ChartSettingsTableFormatting";
import {
  BREAKDOWN_DIMENSION_SETTING,
  COLLAPSED_ROWS_SETTING,
  COLUMN_FORMATTING_SETTING,
  COLUMN_HIDDEN,
  COLUMN_SHOW_TOTALS,
  COLUMN_SORT_ORDER,
  COLUMN_SORT_ORDER_ASC,
  COLUMN_SORT_ORDER_DESC,
  COLUMN_SPLIT_SETTING,
  COLUMN_TOTAL_FORMULA,
  CUSTOM_ACTION_NAME_SETTING,
  CUSTOM_ACTION_RENDER_MODE_SETTING,
  CUSTOM_ACTION_URL_SETTING,
  HEATMAP_MODE_SETTING,
  SHOW_HIDDEN_COLUMNS_SETTING,
  getBreakdownOptions,
  isNativePivotData,
  isPivotGroupColumn,
} from "metabase/visualizations/lib/data_grid";
import { isValidTotalFormula } from "metabase/visualizations/lib/pivotTotalFormula";
import { columnSettings } from "metabase/visualizations/lib/settings/column";
import type { ComputedVisualizationSettings } from "metabase/visualizations/types";
import { migratePivotColumnSplitSetting } from "metabase-lib/v1/queries/utils/pivot";
import {
  getDimensionReferenceWithoutBaseType,
  isDimensionReferenceWithOptions,
} from "metabase-lib/v1/references";
import { isDimension, isMetric } from "metabase-lib/v1/types/utils/isa";
import type {
  Card,
  DatasetColumn,
  DatasetData,
  DimensionReference,
  PivotTableColumnSplitSetting,
  RawSeries,
  Series,
  VisualizationSettings,
} from "metabase-types/api";

import { partitions } from "./partitions";
import {
  addMissingCardBreakouts,
  isColumnValid,
  isFormattablePivotColumn,
  updateValueWithCurrentColumns,
} from "./utils";

export const getTitleForColumn = (
  column: DatasetColumn,
  settings: VisualizationSettings,
) => {
  const { column: _column, column_title: columnTitle } =
    settings.column(column);
  return columnTitle || displayNameForColumn(_column);
};

export const settings = {
  ...columnSettings({ hidden: true }),
  [COLLAPSED_ROWS_SETTING]: {
    hidden: true,
    readDependencies: [COLUMN_SPLIT_SETTING],
    getValue: (
      [{ data }]: Series,
      settings: Partial<VisualizationSettings> = {},
    ) => {
      if (data == null) {
        return undefined;
      }

      // This is hack. Collapsed rows depend on the current column split setting.
      // If the query changes or the rows are reordered, we ignore the current collapsed row setting.
      // This is accomplished by snapshotting part of the column split setting *inside* this setting.
      // `value` the is the actual data for this setting
      // `rows` is value we check against the current setting to see if we should use `value`
      const { rows, value } = settings[COLLAPSED_ROWS_SETTING] || {};
      const { rows: currentRows } = settings[COLUMN_SPLIT_SETTING] || {};
      if (!_.isEqual(rows, currentRows)) {
        // For questions with 2+ row columns (e.g. native SQL with a breakdown),
        // collapse the first level by default so the table is not overwhelming.
        // Use the STRING form "1" (not the number 1): the CLJS engine json-parses
        // it back to an int for level-collapse, while RowToggleIcon compares
        // against JSON.stringify(path.length) — also a string — so per-row
        // expand/collapse toggling works.
        const isNativeQuery = isNativePivotData(data.cols);
        const defaultCollapsed =
          isNativeQuery && (currentRows?.length ?? 0) >= 2 ? ["1"] : [];
        return { value: defaultCollapsed, rows: currentRows };
      }
      // Normalize any numeric level entries (e.g. a previously saved `1`) to the
      // string form so per-row toggling via RowToggleIcon compares correctly.
      const normalizedValue = (value ?? []).map((v: number | string) =>
        typeof v === "number" ? String(v) : v,
      );
      return { rows, value: normalizedValue };
    },
  },
  [COLUMN_SPLIT_SETTING]: {
    get section() {
      return t`Columns`;
    },
    widget: "fieldsPartition",
    persistDefault: true,
    getHidden: ([{ data }]: [{ data: DatasetData }]) =>
      // hide the setting widget if there are invalid columns
      !data || data.cols.some((col) => !isColumnValid(col)),
    getProps: (
      [{ data }]: [{ data: DatasetData }],
      settings: VisualizationSettings,
    ) => ({
      value: migratePivotColumnSplitSetting(
        settings[COLUMN_SPLIT_SETTING] ?? { rows: [], columns: [], values: [] },
        data?.cols ?? [],
      ),
      partitions,
      columns: data == null ? [] : data.cols,
      settings,
      getColumnTitle: (column: DatasetColumn) => {
        return getTitleForColumn(column, settings);
      },
    }),
    getValue: (
      [{ data }]: [{ data: DatasetData; card: Card }],
      settings: Partial<VisualizationSettings> = {},
    ) => {
      const storedValue = settings[COLUMN_SPLIT_SETTING];
      if (data == null) {
        return undefined;
      }
      const columnsToPartition = data.cols.filter(
        (col) => !isPivotGroupColumn(col),
      );
      let setting: PivotTableColumnSplitSetting;
      if (storedValue == null) {
        const isNativeQuery = isNativePivotData(columnsToPartition);
        const [dimensions, values] = _.partition(
          columnsToPartition,
          // For native pivots the backend never tags aggregations, so classify
          // by type: numeric/metric columns are measures, everything else is a
          // dimension.
          isNativeQuery ? (col) => !isMetric(col) : isDimension,
        );
        const [first, second, ...rest] = _.sortBy(dimensions, (col) =>
          getIn(col, ["fingerprint", "global", "distinct-count"]),
        );

        let rows;
        let columns: DatasetColumn[];

        if (isNativeQuery) {
          // Native pivots render all dimensions as nested rows. The Breakdown
          // partition is not shown for native queries so everything goes to rows.
          rows = [first, second, ...rest].filter(Boolean);
          columns = [];
        } else if (dimensions.length < 2) {
          columns = [];
          rows = [first];
        } else if (dimensions.length <= 3) {
          columns = [first];
          rows = [second, ...rest];
        } else {
          columns = [first, second];
          rows = rest;
        }
        setting = _.mapObject({ rows, columns, values }, (cols) =>
          cols.map((col) => col.name),
        );
      } else {
        setting = updateValueWithCurrentColumns(
          storedValue,
          columnsToPartition,
        );
        // For native queries the Breakdown partition no longer exists. Migrate
        // any columns-partition entries from old saved settings into rows. Native
        // pivot settings are always column-name based, so this is name-safe.
        const isNativeQuery = isNativePivotData(columnsToPartition);
        const columnEntries = setting.columns ?? [];
        if (isNativeQuery && columnEntries.length > 0) {
          const rowEntries = setting.rows ?? [];
          setting = {
            rows: [...rowEntries, ...columnEntries] as string[],
            columns: [],
            values: (setting.values ?? []) as string[],
          };
        }
      }

      return addMissingCardBreakouts(setting, columnsToPartition);
    },
  },
  "pivot.show_row_totals": {
    get section() {
      return t`Columns`;
    },
    get title() {
      return t`Show row totals`;
    },
    widget: "toggle",
    // Native SQL has no backend-computed subtotals, so totals rows would be empty.
    getValue: (
      [{ data }]: Series,
      settings: Partial<VisualizationSettings> = {},
    ) => {
      const stored = settings["pivot.show_row_totals"];
      if (stored !== undefined) {
        return stored;
      }
      const isNativeQuery = data != null && isNativePivotData(data.cols);
      return !isNativeQuery;
    },
    inline: true,
  },
  "pivot.show_column_totals": {
    get section() {
      return t`Columns`;
    },
    get title() {
      return t`Show column totals`;
    },
    widget: "toggle",
    getValue: (
      _series: Series,
      settings: Partial<VisualizationSettings> = {},
    ) => {
      const stored = settings["pivot.show_column_totals"];
      if (stored !== undefined) {
        return stored;
      }
      // Native SQL subtotals are synthesized client-side in data_grid.js, so we
      // enable this so the CLJS engine generates subtotal nodes (needed for
      // collapsed rows to display aggregated values).
      return true;
    },
    inline: true,
  },
  "pivot.sort_rows_by_measure": {
    hidden: true,
    getValue: (
      [{ data }]: Series,
      settings: Partial<VisualizationSettings> = {},
    ) => {
      const stored = settings["pivot.sort_rows_by_measure"];
      if (stored !== undefined) {
        return stored;
      }
      // For native SQL pivots with 2+ row dimensions, sort the inner dimension
      // (e.g. country) descending by the first measure (e.g. new_user_count).
      const isNativeQuery = data != null && isNativePivotData(data.cols);
      const columnSplit = settings["pivot_table.column_split"];
      const rowCount = columnSplit?.rows?.length ?? 0;
      return isNativeQuery && rowCount >= 2 ? "desc" : null;
    },
  },
  "pivot.subtotals_on_top": {
    hidden: true,
    getValue: (
      [{ data }]: Series,
      settings: Partial<VisualizationSettings> = {},
    ) => {
      const stored = settings["pivot.subtotals_on_top"];
      if (stored !== undefined) {
        return stored;
      }
      // For native SQL pivots, pin the "Totals for …" row above its children
      // so it stays visible as a summary when the group is expanded.
      return data != null && isNativePivotData(data.cols);
    },
  },
  [BREAKDOWN_DIMENSION_SETTING]: {
    hidden: true,
    getValue: (
      [{ data }]: Series,
      settings: Partial<VisualizationSettings> = {},
    ) => {
      // The chosen inner breakdown for native pivots with 3+ row dims. Defaults
      // to the first secondary dimension; falls back if the stored choice is no
      // longer one of the available secondary dimensions.
      const isNativeQuery = data != null && isNativePivotData(data.cols);
      if (!isNativeQuery) {
        return null;
      }
      const columnSplit = migratePivotColumnSplitSetting(
        settings["pivot_table.column_split"] ?? {
          rows: [],
          columns: [],
          values: [],
        },
        data?.cols ?? [],
      );
      const options = getBreakdownOptions(columnSplit);
      if (options.length === 0) {
        return null;
      }
      const stored = settings[BREAKDOWN_DIMENSION_SETTING];
      return stored != null && options.includes(stored) ? stored : options[0];
    },
  },
  [HEATMAP_MODE_SETTING]: {
    hidden: true,
    getValue: (
      _series: Series,
      settings: Partial<VisualizationSettings> = {},
    ) =>
      // "brand" (default) single-hue ramp, or "diverging" green/red around the
      // column average. Toggled by the "Show heatmap" button in the toolbar.
      settings[HEATMAP_MODE_SETTING] ?? "brand",
  },
  [SHOW_HIDDEN_COLUMNS_SETTING]: {
    hidden: true,
    // View-only override toggled by the "Show hidden columns" toolbar button.
    // Default false: per-column COLUMN_HIDDEN flags are respected.
    getValue: (
      _series: Series,
      settings: Partial<VisualizationSettings> = {},
    ) => settings[SHOW_HIDDEN_COLUMNS_SETTING] ?? false,
  },
  "pivot.condense_duplicate_totals": {
    get section() {
      return t`Columns`;
    },
    get title() {
      return t`Condense duplicate totals`;
    },
    get hint() {
      return t`Hide additional total elements if the totals are the same`;
    },
    widget: "toggle",
    getDefault: () => true,
    inline: true,
    getHidden: (
      _series: RawSeries,
      settings: ComputedVisualizationSettings,
    ) => {
      return (
        !settings["pivot.show_row_totals"] &&
        !settings["pivot.show_column_totals"]
      );
    },
    readDependencies: ["pivot.show_row_totals", "pivot.show_column_totals"],
  },
  "pivot_table.column_widths": {},
  [COLUMN_FORMATTING_SETTING]: {
    get section() {
      return t`Conditional Formatting`;
    },
    widget: ChartSettingsTableFormatting,
    getDefault: (
      [{ data }]: [{ data: DatasetData }],
      settings: VisualizationSettings,
    ) => {
      const columnFormats = settings[COLUMN_FORMATTING_SETTING] ?? [];

      return columnFormats
        .map((columnFormat) => {
          const hasOnlyFormattableColumns =
            columnFormat.columns
              .map((columnName: string) =>
                data.cols.find((column) => column.name === columnName),
              )
              .filter(Boolean) ?? [].every(isFormattablePivotColumn);

          if (!hasOnlyFormattableColumns) {
            return null;
          }

          return {
            ...columnFormat,
            highlight_row: false,
          };
        })
        .filter(Boolean);
    },
    isValid: (
      [{ data }]: [{ data: DatasetData }],
      settings: VisualizationSettings,
    ): boolean => {
      const columnFormats = settings[COLUMN_FORMATTING_SETTING] ?? [];

      return columnFormats.every((columnFormat) => {
        const hasOnlyFormattableColumns =
          columnFormat.columns
            .map((columnName) =>
              (data.cols as DatasetColumn[]).find(
                (column) => column.name === columnName,
              ),
            )
            .filter(Boolean) ?? [].every(isFormattablePivotColumn);

        return (
          hasOnlyFormattableColumns &&
          columnFormat.type === "single" &&
          !columnFormat.highlight_row
        );
      });
    },
    getProps: (series: Series) => {
      const cols = series[0].data?.cols ?? [];

      return {
        canHighlightRow: false,
        cols: cols.filter(isFormattablePivotColumn),
      };
    },
    getHidden: ([{ data }]: [{ data: DatasetData }]) =>
      !data?.cols.some((col) => isFormattablePivotColumn(col)),
  },
  [CUSTOM_ACTION_NAME_SETTING]: {
    get section() {
      return t`Custom Action`;
    },
    get title() {
      return t`Action name`;
    },
    get hint() {
      return t`Label for the right-click action on the first column, e.g. "Predict". Leave blank to disable.`;
    },
    widget: "input",
    getProps: () => ({ placeholder: t`e.g. Predict` }),
    getDefault: () => "",
  },
  [CUSTOM_ACTION_URL_SETTING]: {
    get section() {
      return t`Custom Action`;
    },
    get title() {
      return t`POST URL`;
    },
    get hint() {
      return t`The service that receives the row's data as a JSON POST and returns HTML to display. The request is proxied through the server, so HTTP (non-HTTPS) URLs are allowed.`;
    },
    widget: "input",
    getProps: () => ({ placeholder: t`e.g. https://example.com/predict` }),
    getDefault: () => "",
  },
  [CUSTOM_ACTION_RENDER_MODE_SETTING]: {
    get section() {
      return t`Custom Action`;
    },
    get title() {
      return t`Render mode`;
    },
    get hint() {
      return t`How to render the service's response: HTML renders the response directly; Retention projection parses a JSON response and renders it as a retention report.`;
    },
    widget: "select",
    getDefault: () => "html",
    getProps: () => ({
      options: [
        { name: t`HTML`, value: "html" },
        { name: t`Retention projection`, value: "retention_projection" },
      ],
    }),
  },
};

export const _columnSettings = {
  [COLUMN_SORT_ORDER]: {
    get title() {
      return t`Sort order`;
    },
    widget: ChartSettingIconRadio,
    inline: true,
    getWrapperStyle: () => ({
      paddingBottom: "1rem",
      borderBottom: `1px solid var(--mb-color-border)`,
    }),
    getProps: () => ({
      options: [
        {
          iconName: "arrow_up",
          value: COLUMN_SORT_ORDER_ASC,
        },
        {
          iconName: "arrow_down",
          value: COLUMN_SORT_ORDER_DESC,
        },
      ],
    }),
    getHidden: ({ source }: { source: DatasetColumn["source"] }) =>
      source === "aggregation",
  },
  [COLUMN_SHOW_TOTALS]: {
    get title() {
      return t`Show totals`;
    },
    widget: "toggle",
    inline: true,
    getDefault: (
      column: DatasetColumn,
      columnSettings: DatasetColumn,
      {
        settings,
        series,
      }: { settings: VisualizationSettings; series?: Series },
    ) => {
      // For native SQL the empty subtotal rows are already suppressed by
      // `pivot.show_column_totals` being false. We keep this per-column setting
      // TRUE so the row stays collapsible: filter-collapsed-subtotals in the
      // CLJS engine drops collapse entries for columns whose show-totals is
      // false, which would otherwise break expand/collapse and the
      // collapsed-by-default behavior.
      const data = series?.[0]?.data;
      const isNativeQuery = data != null && isNativePivotData(data.cols);
      if (isNativeQuery) {
        return true;
      }

      // Default to showing totals if appropriate
      const rows = settings[COLUMN_SPLIT_SETTING]?.rows || [];

      // `rows` can be either in a legacy format where it's a field ref, or in a new format where it's a column name.
      // All new questions visualized as pivot tables will have column name settings only. We migrate
      // `COLUMN_SPLIT_SETTING` for existing questions only on an explicit change from the user; therefore we need to
      // support both column names and field refs for now.
      return rows
        .slice(0, -1)
        .some(
          (row) =>
            _.isEqual(row, column.name) ||
            (Array.isArray(row) &&
              column.field_ref != null &&
              _.isEqual(
                getFieldRefForComparison(row),
                getFieldRefForComparison(column.field_ref),
              )),
        );
    },
    getHidden: (
      column: DatasetColumn,
      columnSettings: DatasetColumn,
      { settings }: { settings: VisualizationSettings },
    ) => {
      const rows = settings[COLUMN_SPLIT_SETTING]?.rows || [];
      // to show totals a column needs to be:
      //  - in the left header ("rows" in COLUMN_SPLIT_SETTING)
      //  - not the last column
      return !rows.slice(0, -1).some((row) => _.isEqual(row, column.name));
    },
  },
  column_title: {
    get title() {
      return t`Column title`;
    },
    widget: "input",
    getDefault: displayNameForColumn,
  },
  [COLUMN_HIDDEN]: {
    get title() {
      return t`Hide column`;
    },
    get hint() {
      return t`Hide this measure from the table. It can still be referenced by other columns' custom total formulas.`;
    },
    widget: "toggle",
    inline: true,
    getDefault: () => false,
    // Only measures (value columns) can be hidden.
    getHidden: (
      column: DatasetColumn,
      _columnSettings: DatasetColumn,
      { settings }: { settings: VisualizationSettings },
    ) => !isValueColumn(column, settings),
  },
  [COLUMN_TOTAL_FORMULA]: {
    get title() {
      return t`Custom total formula`;
    },
    get hint() {
      return t`Compute this column's totals from other columns' aggregated totals, e.g. rev_d0 / spend_d0 * 100. Leave blank to use the default sum or weighted average.`;
    },
    widget: "input",
    getProps: () => ({ placeholder: t`e.g. rev_d0 / spend_d0 * 100` }),
    getDefault: () => "",
    isValid: (
      _column: DatasetColumn,
      columnSettings: { [COLUMN_TOTAL_FORMULA]?: string },
    ) => isValidTotalFormula(columnSettings[COLUMN_TOTAL_FORMULA] ?? ""),
    getHidden: (
      column: DatasetColumn,
      _columnSettings: DatasetColumn,
      { settings }: { settings: VisualizationSettings },
    ) => !isValueColumn(column, settings),
  },
};

// True if `column` is in the "values" (measures) partition of the pivot split.
function isValueColumn(
  column: DatasetColumn,
  settings: VisualizationSettings,
): boolean {
  if (column.source === "aggregation") {
    return true;
  }
  const values = settings[COLUMN_SPLIT_SETTING]?.values ?? [];
  return values.some((name) => name === column.name);
}

/*
  When comparing field refs for pivot viz settings, ignore `base-type`.
  Sometimes it's present, sometimes it's not. New pivot settings use column
  names only and do not depend on field refs.
 */
function getFieldRefForComparison(fieldRef: DimensionReference) {
  return isDimensionReferenceWithOptions(fieldRef)
    ? getDimensionReferenceWithoutBaseType(fieldRef)
    : fieldRef;
}
